import { usePeriod } from '../../payload/PeriodContext'
import { chartTheme } from '../charts/chartTheme'
import {
  BarsH,
  Card,
  CardHead,
  DataTable,
  HelpMark,
  InlineBar,
  Note,
  SectionHead,
  Td,
  Tr,
} from '../components'
import { day, listOf, n, num, plural, s } from '../format'
import { useBlank } from './useBlank'

/** What gets used, and what loses people. Reach is people; volume is how often. */
const Features = () => {
  const { panel, period, inWindowPhrase } = usePeriod()
  const blank = useBlank()
  const areas = panel('feature_areas')
  const feats = panel('feature_reach')
  const topics = panel('topic_coverage')
  const surveys = panel('survey_reach')
  // `unmeasured` is deliberately not rendered: the list of features that report
  // no usage is a QA concern, and the "we cannot measure this" note below reads
  // features_live off the areas panel instead.

  const featMax = Math.max(
    1,
    ...feats.map(f => Math.max(n(f.reach), n(f.other_reach)))
  )
  const topicMax = Math.max(
    1,
    ...topics.map(t => Math.max(n(t.users), n(t.all_users)))
  )
  const neverUsed = topics.filter(t => n(t.all_users_ever) === 0)
  const teamOnly = topics.filter(
    t => n(t.users_ever) === 0 && n(t.all_users_ever) > 0
  )
  const quietHere = topics.filter(
    t => n(t.all_users) === 0 && n(t.all_users_ever) > 0
  )

  const teamOnlyAreas = areas.filter(
    a => n(a.real_reach) === 0 && (n(a.staff_reach) > 0 || n(a.geo_reach) > 0)
  )
  const blindAreas = areas.filter(a => n(a.features_live) === 0)
  const provenAreas = areas.filter(a => n(a.real_reach) > 0)

  return (
    <section className="grid gap-4">
      <SectionHead
        title="Features"
        question="What gets used, and what loses people? Reach counts anyone who touched an area; volume counts how often."
      />

      <Card>
        <CardHead
          title="Which parts of the product get used"
          info={[
            'Only things people actually do with the product are counted. Opening a page or switching a tab is movement, not use — counting it would put "page view" at the top of every chart and tell us nothing.',
            'Split by who used it, because "nobody wants this" and "nobody outside the team has tried it" look identical on a single bar and call for opposite decisions.',
            'Rows come from the catalogue of features the app has, not from the activity — so a part of the product nobody touched shows as a zero instead of disappearing off the chart.',
          ]}
        />
        {areas.length ? (
          <BarsH
            rows={areas}
            labelKey="area"
            label="Which parts of the product get used"
            labelWidth={180}
            strongLabels
            series={[
              {
                key: 'real_reach',
                label: 'Real users',
                color: chartTheme.series[0],
              },
              {
                key: 'geo_reach',
                label: 'Team (outside the US)',
                color: chartTheme.series[2],
              },
              {
                key: 'staff_reach',
                label: 'Team and test accounts',
                color: chartTheme.series[1],
              },
            ]}
            tipExtra={row => [
              ['Early web-app signups', num(row.legacy_reach)],
              ['Features built here', num(row.features_instrumented)],
              ['Of those, ones we can measure', num(row.features_live)],
              ['Ones a real user has touched', num(row.features_touched)],
            ]}
          />
        ) : (
          blank(
            'feature_areas',
            'No product features were used in this period.'
          )
        )}
        <Note
          flag
          parts={
            teamOnlyAreas.length > 0 && [
              <strong key="lead">
                {`No real user has opened ${listOf(teamOnlyAreas.map(a => s(a.area)))} ${inWindowPhrase}. `}
              </strong>,
              teamOnlyAreas.length === 1
                ? `Only ${plural(n(teamOnlyAreas[0].staff_reach) + n(teamOnlyAreas[0].geo_reach), 'team account')} did.`
                : `Only the team did — ${listOf(teamOnlyAreas.map(a => `${plural(n(a.staff_reach) + n(a.geo_reach), 'account')} in ${s(a.area)}`))}.`,
              ' That is a different problem from nobody wanting it: it may simply not have been put in front of anyone yet.',
            ]
          }
        />
        <Note
          flag
          parts={
            blindAreas.length > 0 && [
              <strong key="lead">
                {`We cannot measure ${listOf(blindAreas.map(a => s(a.area)))} at all. `}
              </strong>,
              listOf(
                blindAreas.map(
                  a =>
                    `${s(a.area)} has ${plural(n(a.features_instrumented), 'feature')} built`
                )
              ),
              ', and none of them report usage — so the zero above is a blind spot, not a verdict. Nothing can be concluded either way until they are tracked.',
            ]
          }
        />
        <Note
          parts={
            provenAreas.length > 0 && [
              `${listOf(provenAreas.map(a => s(a.area)))} `,
              provenAreas.length === 1
                ? 'is the only part'
                : 'are the only parts',
              ' of the product a real user touched ',
              `${inWindowPhrase}.`,
            ]
          }
        />
      </Card>

      <Card>
        <CardHead
          title="Opened a section, versus filling it in"
          info={[
            'Both bars count the same real users on the same scale, so the gap between them is the drop from having a look to committing something.',
            '"Opened" comes from activity, "saved data" from the app database — neither source can produce both numbers, which is why they sit side by side rather than as one funnel.',
            "Hover a row for how many answers were saved and how many of the section's topics were touched.",
          ]}
        />
        {surveys.length ? (
          <BarsH
            rows={surveys}
            labelKey="survey"
            label="Opened a section versus filling it in"
            labelWidth={190}
            series={[
              {
                key: 'opened',
                label: 'Opened it',
                color: chartTheme.series[0],
              },
              {
                key: 'users_with_data',
                label: 'Saved data in it',
                color: chartTheme.series[1],
              },
            ]}
            tipExtra={row => [
              ['Answers saved', num(row.submissions)],
              [
                'Topics used',
                `${num(row.topics_used)} of ${num(row.topics_available)}`,
              ],
            ]}
          />
        ) : (
          blank(
            'survey_reach',
            'No sections were opened or filled in during this period.'
          )
        )}
      </Card>

      <Card>
        <CardHead
          title="Feature by feature"
          info={[
            '"Times each" is how often a real user who used a feature used it in total — close to 1 means they tried it once and moved on.',
            '"On more than one day" counts real users who came back to it later, inside the selected period.',
            'Reach is classified by account, not by the event flag: the looser filter counted team members identifiable only by country, and early web-app accounts, as real users.',
            'Retired features are excluded — four were replaced in a mid-August instrumentation change, and charting one beside its replacement would double-count the same behaviour.',
          ]}
        />
        {feats.length ? (
          <DataTable
            label="Feature by feature"
            head={[
              { label: 'Feature' },
              { label: 'Part of the product' },
              { label: '' },
              { label: 'Real users', num: true },
              { label: 'Team & others', num: true },
              { label: 'Times each', num: true },
              { label: 'On 2+ days', num: true },
              { label: 'Last used' },
            ]}
          >
            {feats.map(f => (
              <Tr key={s(f.event_key) || s(f.feature)} dim={n(f.reach) === 0}>
                <Td>
                  {s(f.feature)}
                  <HelpMark text={s(f.description)} />
                </Td>
                <Td muted>{s(f.area)}</Td>
                <Td>
                  <InlineBar
                    value={n(f.reach)}
                    max={featMax}
                    hint={`${plural(n(f.reach), 'real user')} · ${plural(n(f.other_reach), 'other account')}`}
                  />
                </Td>
                <Td num>{num(f.reach)}</Td>
                <Td num muted>
                  {num(f.other_reach)}
                </Td>
                <Td num>
                  {f.intensity === null || f.intensity === undefined
                    ? '—'
                    : s(f.intensity)}
                </Td>
                <Td num>{num(f.sticky_actors)}</Td>
                <Td muted nowrap>
                  {f.last_used ? day(f.last_used) : 'never'}
                </Td>
              </Tr>
            ))}
          </DataTable>
        ) : (
          blank(
            'feature_reach',
            'No product features were used in this period.'
          )
        )}
      </Card>

      <Card>
        <CardHead
          title="Which financial topics get filled in"
          info={[
            'The most trustworthy usage measure on this page: a saved answer is a record in the app database, not an event, so it cannot be lost to an ad blocker or missing tracking.',
            'Every topic in the product is listed, so one nobody has touched shows as a zero rather than disappearing off the bottom.',
            '"All accounts" is a trap if read alone — most of the answers in the database belong to the team, to test accounts, or to the early web-app cohort.',
          ]}
        />
        {topics.length ? (
          <DataTable
            label="Which financial topics get filled in"
            head={[
              { label: 'Topic' },
              { label: 'Section' },
              { label: '' },
              { label: 'Real users', num: true },
              { label: 'All accounts', num: true },
              { label: 'Answers each', num: true },
              { label: 'Last saved' },
            ]}
          >
            {topics.map(t => (
              <Tr key={s(t.topic_id) || s(t.topic)} dim={n(t.all_users) === 0}>
                <Td>
                  {t.icon ? `${s(t.icon)} ` : ''}
                  {s(t.topic)}
                </Td>
                <Td muted>{s(t.survey)}</Td>
                <Td>
                  <InlineBar
                    value={n(t.users)}
                    max={topicMax}
                    hint={`${plural(n(t.users), 'real user')} · ${plural(n(t.all_users), 'account')} in total`}
                  />
                </Td>
                <Td num>{num(t.users)}</Td>
                <Td num muted>
                  {num(t.all_users)}
                </Td>
                <Td num muted>
                  {t.avg_answers === null || t.avg_answers === undefined
                    ? '—'
                    : s(t.avg_answers)}
                </Td>
                <Td muted nowrap>
                  {t.last_used ? day(t.last_used) : 'never'}
                </Td>
              </Tr>
            ))}
          </DataTable>
        ) : (
          blank(
            'topic_coverage',
            'No topics were filled in during this period.'
          )
        )}
        <Note
          flag
          parts={
            neverUsed.length > 0 && [
              <strong key="lead">
                {`${plural(neverUsed.length, 'topic')} ${neverUsed.length === 1 ? 'has ' : 'have '}never been filled in by anyone, in any period: `}
              </strong>,
              listOf(neverUsed.map(t => `${s(t.topic)} (${s(t.survey)})`)),
              '.',
            ]
          }
        />
        <Note
          parts={
            teamOnly.length > 0 && [
              <strong key="lead">
                {`Another ${plural(teamOnly.length, 'topic')} ${teamOnly.length === 1 ? 'has ' : 'have '}only ever been filled in by the team: `}
              </strong>,
              listOf(teamOnly.map(t => `${s(t.topic)} (${s(t.survey)})`)),
              '.',
            ]
          }
        />
        <Note
          parts={
            period.key !== 'all' &&
            quietHere.length > 0 && [
              `${plural(quietHere.length, 'other topic')} `,
              quietHere.length === 1 ? 'has ' : 'have ',
              `had nothing saved ${period.label.toLowerCase()}, though `,
              quietHere.length === 1 ? 'it has' : 'they have',
              ' been used before.',
            ]
          }
        />
      </Card>
    </section>
  )
}

export default Features
